import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, Search, ArrowLeft, Loader2, FileText, AlertTriangle,
  CheckCircle2, XCircle, Clock,
} from "lucide-react";

const NAVY = "#0B1B2F";
const GOLD = "#C3A05A";

const STATUS_STYLE = {
  FOUND:    { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-300", label: "ENCONTRADO" },
  NOT_FOUND:{ icon: XCircle,      cls: "bg-slate-50 text-slate-500 border-slate-300",       label: "SEM REGISTRO" },
  SOURCE_UNAVAILABLE: { icon: Clock, cls: "bg-amber-50 text-amber-700 border-amber-300",   label: "FONTE INDISPONÍVEL" },
  NOT_QUERIED: { icon: XCircle,   cls: "bg-slate-50 text-slate-400 border-slate-300",       label: "NÃO CONSULTADO" },
};

function StatusPill({ status }) {
  const cfg = STATUS_STYLE[status] || STATUS_STYLE.NOT_QUERIED;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] tracking-widest px-2 py-1 rounded border ${cfg.cls}`}>
      <Icon className="w-3 h-3"/> {cfg.label}
    </span>
  );
}

export default function Fase6Contratacoes() {
  const [cnpj, setCnpj] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const doSearch = async () => {
    if (!cnpj.replace(/\D/g, "").length) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.get(`/intel/contratacoes-360/${cnpj.replace(/\D/g,"")}`);
      setResult(data);
    } catch (e) {
      toast.error("Erro ao consultar cruzamento contratações-360");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="fase6-contratacoes-page">
      <header className="bg-[#0B1B2F] text-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center gap-3">
          <Link to="/inteligencia" data-testid="back-intel"
                className="text-white/70 hover:text-white flex items-center gap-1 text-sm">
            <ArrowLeft className="w-4 h-4"/> Inteligência 360
          </Link>
          <span className="text-white/30">·</span>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                 style={{ background: `${GOLD}33`, border: `1px solid ${GOLD}` }}>
              <Building2 className="w-4 h-4" style={{ color: GOLD }}/>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: GOLD }}>
                Fase 6 · Contratações Públicas
              </div>
              <h1 className="font-heading text-xl">PNCP · TCU · CVM · CNO por CNPJ</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <label className="text-xs uppercase tracking-widest text-slate-500">CNPJ</label>
          <div className="mt-2 flex gap-2">
            <input value={cnpj} onChange={(e) => setCnpj(e.target.value)}
              placeholder="00.000.000/0000-00" data-testid="input-cnpj"
              className="flex-1 border border-slate-300 rounded-lg px-3 py-2.5 text-sm font-mono"/>
            <button onClick={doSearch} disabled={busy || !cnpj}
              data-testid="btn-buscar-contratacoes"
              className="px-5 py-2.5 rounded-lg text-white text-sm font-semibold
                             disabled:opacity-60 inline-flex items-center gap-2"
              style={{ background: NAVY }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Search className="w-4 h-4"/>}
              Cruzar 4 fontes
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Cruzamento oficial de PNCP (contratos), TCU (inabilitados via CGU),
            CVM (mercado) e CNO (obras). Nada inventado.
          </p>
        </div>

        {result && (
          <section className="mt-6 space-y-4" data-testid="results-contratacoes">
            {/* Summary */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">
                Cruzamento Fase 6 · CNPJ {result.identifier}
              </div>
              <div className="mt-1 font-heading text-2xl" style={{ color: NAVY }}>
                {result.summary?.verdict}
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                <span>Fontes consultadas: <b>{result.summary?.sources_queried}</b></span>
                <span>·</span>
                <span>Fontes com dados: <b>{result.summary?.sources_found}</b></span>
              </div>
            </div>

            {/* PNCP */}
            <SourceCard
              testid="src-pncp" title="PNCP · Contratos Públicos" data={result.pncp}
              renderRow={(r) => (
                <>
                  <div className="text-xs text-slate-500">{r.numero_controle_pncp}</div>
                  <div className="text-sm font-medium">
                    Órgão: {r.orgao_razao} · Fornecedor: {r.fornecedor_razao}
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    {(r.objeto || "").slice(0, 200)}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Valor R$ {r.valor_inicial} · {r.data_assinatura}
                  </div>
                </>
              )}
            />

            {/* TCU */}
            <SourceCard
              testid="src-tcu" title="TCU · Inabilitados" data={result.tcu_inabilitados}
              renderRow={(r) => (
                <>
                  <div className="text-sm font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-rose-600"/>
                    {r.nome}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Processo {r.processo} · {r.data_inicio} → {r.data_final || "vigente"}
                  </div>
                </>
              )}
            />

            {/* CVM */}
            <SourceCard testid="src-cvm" title="CVM · Participantes do Mercado" data={result.cvm}/>
            {/* CNO */}
            <SourceCard testid="src-cno" title="CNO · Cadastro Nacional de Obras" data={result.cno}/>
          </section>
        )}
      </main>
    </div>
  );
}

function SourceCard({ title, data, renderRow, testid }) {
  const status = data?.status || "NOT_QUERIED";
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4" style={{ color: GOLD }}/>
          <h3 className="font-semibold" style={{ color: NAVY }}>{title}</h3>
        </div>
        <StatusPill status={status}/>
      </div>
      {status === "FOUND" ? (
        <div className="space-y-2">
          {(data.records || []).slice(0, 10).map((r, i) => (
            <div key={i} className="text-sm border-l-2 border-slate-200 pl-3 py-1.5">
              {renderRow ? renderRow(r) : (
                <pre className="text-xs text-slate-600 whitespace-pre-wrap">
                  {JSON.stringify(r, null, 2)}
                </pre>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs text-slate-500">
          {data?.reason || "Sem registros retornados pela fonte."}
        </div>
      )}
    </div>
  );
}
